import { Component, OnDestroy, OnInit, inject } from "@angular/core";
import { Utilities } from "@tylertech/shared-module";
import { LoggingService, NavigationService } from "@tylertech/core-module";
import { ICustomViewComponent } from "@tylertech/custom-view";
import { ChartBindingObject, ChartManagerService, ChartRegistryService } from "@tylertech/charting-module";
import { IChartCriteria, ProfileSettingViewModel } from "@tylertech/tyler-api-types";
import { OpenEnrollmentCompleteData } from "../../../interfaces/openEnrollmentCompleteData";
import { OpenEnrollmentChartService } from "../../../services";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { HttpErrorResponse } from "@angular/common/http";
@Component({
    selector: "payroll-open-enrollment-chart",
    templateUrl: "./openEnrollmentChart.tmpl.html",
    standalone: false
})
export class OpenEnrollmentChartComponent implements OnDestroy, OnInit, ICustomViewComponent<ChartBindingObject>
{
    private readonly $chartRegistryService = inject(ChartRegistryService);
    private readonly $loggingService = inject(LoggingService);
    private readonly $navigationService = inject(NavigationService);
    private readonly $openEnrollmentChartService = inject(OpenEnrollmentChartService);

    private readonly destructor$ = new Subject<void>();
    readonly customDataBinding: ChartBindingObject;
    private _chartCriteria: IChartCriteria;

    ChartData: OpenEnrollmentCompleteData[];
    ChartHasData: boolean;
    ChartSubtitle: string;
    ChartTitle: string;
    chartLoading: boolean;
    hidden: boolean = true;
    isRefreshing: boolean;
    showSettings: boolean;
    showTotal: boolean = true;

    constructor()
    {
        const $chartManagerService = inject(ChartManagerService);

        $chartManagerService.refreshChartObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe((chartId: string) =>
            {
                if (chartId !== this.chartId)
                {
                    return;
                }
                this.loadChartData();
            });
    }

    get chartId(): string | undefined
    {
        return this.customDataBinding?.chartId;
    }

    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria || this.customDataBinding?.chartCriteria;
    }

    set chartCriteria(value: IChartCriteria) { this._chartCriteria = value; }

    get showChart(): boolean
    {
        return !!this.chartCriteria?.ErrorMessage || this.hasData();
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        const param = <ProfileSettingViewModel>{
            Id: "Total",
            Value: this.showTotal,
        };
        this.chartCriteria?.Parameters?.push(param);
        this.isRefreshing = true;
        new Promise((_resolveFn, rejectFn) =>
        {
            if (!this.hasData())
            {
                return this.loadChartData();
            }
            try
            {
                this.updateChartConfiguration();
            }
            catch (err)
            {
                rejectFn(err);
            }
        })
            .catch(e =>
            {
                console.error("Error on load", e);
            })
            .finally(() =>
            {
                this.isRefreshing = false;
            });
    }

    drillIn(): void
    {
        const code = this.$openEnrollmentChartService.GetPlanCode();
        if (this.showTotal)
        {
            this.chartCriteria.Parameters.filter(x => x.Id === "Total")[0].Value = false;
            this.loadChartData();
            this.showTotal = false;
        }
        else if (!Utilities.IsNullOrUndefinedOrEmpty(code))
        {
            this.$navigationService.navigateToApp("personnelII/employee/directory", { BenefitPlan: code });
        }
    }

    hasData(): boolean
    {
        return this.chartCriteria?.ChartDatas?.length > 0;
    }

    private loadChartData(): Promise<void>
    {
        this.isRefreshing = true;
        this.chartLoading = true;
        return this.$chartRegistryService
            .GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                    if (this.chartCriteria.ErrorMessage)
                    {
                        return;
                    }
                    this.isRefreshing = true;
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                }
            )
            .then(
                () =>
                {
                    if (!this.hasData())
                    {
                        return;
                    }
                    try
                    {
                        this.updateChartConfiguration();
                    }
                    catch (err)
                    {
                        return Promise.reject(err);
                    }
                },
                (err: unknown) =>
                {
                    console.error(err);
                }
            )
            .finally(() =>
            {
                if (!this.chartCriteria.ErrorMessage)
                {
                    this.chartLoading = false;
                }
                setTimeout(() =>
                {
                    this.isRefreshing = false;
                }, 50);
            });
    }

    private updateChartConfiguration(): void
    {
        if (
            this.chartCriteria?.ChartDatas?.length === 0 ||
            this.chartCriteria.ChartDatas[0].Data.length === 0 ||
            this.chartCriteria.ChartDatas[0].Data[0].length === 0
        )
        {
            this.hidden = true;
            this.ChartHasData = false;
            this.ChartData = null;
        }
        else
        {
            this.hidden = false;
            this.ChartHasData = true;
            this.ChartData = this.chartCriteria.ChartDatas[0].Data[0];
            this.$openEnrollmentChartService.SetData(this.ChartData);
        }
    }

    get hasDataCompleteCard(): boolean
    {
        return !!this.data;
    }

    get data(): OpenEnrollmentCompleteData[]
    {
        return this.$openEnrollmentChartService.GetData();
    }
}
